import { FleetOperationalStatus, PermissionCode } from '@gb-ticket/shared-types';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { fleetPanelClassName } from '@/products/fleet/features/fleet-panel';
import {
  useFleetOdometer,
  useFleetVehicles,
  useRecordFleetOdometer,
} from '@/products/fleet/features/use-fleet';

export function FleetOdometerPage() {
  usePageTitle('Kilometerstand');
  const copy = useVoiceCopy();
  const me = useMe();
  const manage = hasPermission(me.data, PermissionCode.FLEET_VEHICLES_MANAGE);
  const vehicles = useFleetVehicles(false, {
    ...(manage ? {} : { mine: true as const }),
    operationalStatus: FleetOperationalStatus.IN_SERVICE,
  });
  const [vehicleId, setVehicleId] = useState('');
  const odometer = useFleetOdometer(vehicleId);
  const record = useRecordFleetOdometer(vehicleId);
  const [readingKm, setReadingKm] = useState('');
  const [warning, setWarning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const ownVehicles = vehicles.data?.items ?? [];

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setWarning(null);
    if (!vehicleId) {
      setError('Bitte ein Fahrzeug wählen.');
      return;
    }
    const km = Number(readingKm);
    if (!Number.isFinite(km) || km < 0) {
      setError('Bitte einen gültigen Kilometerstand eingeben.');
      return;
    }
    try {
      const result = await record.mutateAsync({ readingKm: km });
      setReadingKm('');
      if (result.warning) {
        setWarning(result.warning);
        toast.success('Kilometerstand gespeichert.', { description: result.warning });
      } else {
        toast.success('Kilometerstand gespeichert.');
      }
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? err.message
          : 'Der Kilometerstand konnte nicht gespeichert werden.';
      setError(message);
      toast.error(message);
    }
  }

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Kilometerstand"
        description={
          manage
            ? 'Stand für ein einsatzbereites Fahrzeug melden.'
            : copy('fleet.odometer.description')
        }
      />
      {vehicles.isPending ? (
        <p className="text-sm text-muted-foreground">Fahrzeuge werden geladen …</p>
      ) : null}
      {ownVehicles.length === 0 && !vehicles.isPending ? (
        <p className="text-sm text-muted-foreground">
          {manage ? 'Kein einsatzbereites Fahrzeug.' : copy('fleet.vehicle.none-today')}
        </p>
      ) : null}
      <form className={`${fleetPanelClassName} space-y-4`} onSubmit={onSubmit}>
        <div className="space-y-1">
          <Label htmlFor="odo-vehicle">Fahrzeug</Label>
          <Select
            value={vehicleId || 'none'}
            onValueChange={(value) => {
              setVehicleId(value === 'none' ? '' : value);
              setWarning(null);
              setError(null);
            }}
          >
            <SelectTrigger id="odo-vehicle" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Bitte wählen</SelectItem>
              {ownVehicles.map((vehicle) => (
                <SelectItem key={vehicle.id} value={vehicle.id}>
                  {vehicle.licensePlate} · {vehicle.make} {vehicle.model}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {vehicleId ? (
          <p className="text-sm text-muted-foreground">
            Letzter Stand:{' '}
            {odometer.isPending
              ? '…'
              : odometer.data?.currentKm != null
                ? `${odometer.data.currentKm.toLocaleString('de-DE')} km`
                : 'noch keiner'}
          </p>
        ) : null}
        <div className="space-y-1">
          <Label htmlFor="odo-km">Neuer Stand (km)</Label>
          <Input
            id="odo-km"
            type="number"
            min={0}
            step={1}
            inputMode="numeric"
            value={readingKm}
            onChange={(event) => setReadingKm(event.target.value)}
            required
          />
        </div>
        {warning ? <p className="text-sm text-amber-700 dark:text-amber-400">{warning}</p> : null}
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <Button type="submit" disabled={record.isPending || !vehicleId}>
          Senden
        </Button>
      </form>
    </div>
  );
}
